/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { Injectable } from '@angular/core';
import { HttpClient, HttpContext, HttpErrorResponse } from '@angular/common/http';
import { catchError, Observable, of } from 'rxjs';

import { PROCORE_PROJECT_ACCESS_DENIED_REAUTHENTICATE } from '@touchplan/strings';
import { TOUCHPLAN_API_CONTEXT } from './rest-api-configuration/touchplan-rest-interceptor';
import {
	PROCORE_AUTHENTICATION_ROUTE,
	PROCORE_CREATE_RFI_ROUTE,
	PROCORE_GET_RFI_ROUTE,
	PROCORE_LIST_RFI_ROUTE,
	PROCORE_LOGIN_WITH_ACCESS_CODE_ROUTE,
} from './rest-api-configuration/rest-api-routes';
import { BannerMessageHandler } from '../common/logging/banner-message-handler';
import { LoggerServiceFactory } from '../common/logging/LoggerServiceFactory';

// Interface Procore uses to create a new RFI on a project
export interface IProcoreRFIInput {
	subject: string;
	questionBody: string;
}

export enum ProcoreRFIStatus {
	closed = 'closed',
	open = 'open',
	draft = 'draft',
}

export interface IProcoreRFI {
	id: string;
	created_at: string;
	initiated_at: string;
	link: string;
	assignees: IProcoreUser[];
	due_date: string;
	number: string;
	private: boolean;
	subject: string;
	updated_at: string;
	status: ProcoreRFIStatus;
	time_resolved: string;
	assignee: IProcoreUser;
	questions: IProcoreRFIQuestion[];
	created_by: IProcoreUser;
	//unclear if these are actually real, but they're used in the template
	translated_status?: string;
}

export interface IProcoreUser {
	id: number;
	name: string;
	login?: string;
	response_required?: boolean;
}

export interface IProcoreRFIQuestion {
	id: number;
	body: string;
	//unclear if these are actually real, but they're used in the template
	plain_text_body?: string;
	answers?: { plain_text_body: string }[];
}

@Injectable()
export class ProcoreService {
	private logger = LoggerServiceFactory.create('ProcoreService');
	// The "ProcoreInterceptor" will be applied to all requests that include the URL below.

	constructor(private http: HttpClient, private bannerMessage: BannerMessageHandler) {}

	/**
	 * This method allow user to complete the authentication flow using an Procore access code
	 * @param accessCode string with Procore access code
	 * @returns a observable of the string with token object in plain text
	 */
	public loginWithAccessCode(accessCode: string): Observable<string> {
		return this.http.post<string>(
			PROCORE_LOGIN_WITH_ACCESS_CODE_ROUTE,
			{},
			{
				params: { code: accessCode },
				context: new HttpContext().set(TOUCHPLAN_API_CONTEXT, {
					responseType: 'text',
					withCredentials: true,
				}),
			}
		);
	}

	/**
	 * This method authenticate user against Procore
	 * @param projectId string with Touchplan project Id
	 * @param userId string with Touchplan user Id
	 * @param procoreProjectId Procore project Id
	 * @returns a string of token object if user is authenticated or a redirect URL to perfom login if is not
	 */
	public authenticate(projectId: string, userId: string, procoreProjectId: string): Observable<string> {
		return this.http.get<string>(PROCORE_AUTHENTICATION_ROUTE(userId, projectId, procoreProjectId), {
			context: new HttpContext().set(TOUCHPLAN_API_CONTEXT, {
				responseType: 'text',
				withCredentials: true,
			}),
		});
	}

	/**
	 * Get the RFIs associated a given Procore project id
	 * See Procore docs: https://developers.procore.com/reference/rfis
	 *
	 * @param procoreProjectId string with Procore project Id
	 * @param procoreCompanyId  string with Procore company Id
	 * @param filters an object with configured filters
	 * @returns an array of RFIs for the given Procore project.
	 */
	public listRFIs(
		procoreProjectId: string,
		procoreCompanyId: string,
		filters: {
			filterRfiIds?: string[];
		} = {}
	): Observable<IProcoreRFI[]> {
		return this.http
			.post<IProcoreRFI[]>(PROCORE_LIST_RFI_ROUTE(procoreProjectId, procoreCompanyId), {
				...filters,
			})
			.pipe(
				catchError((error) => {
					this.logger.error('Error during get list of RFIs:', error);
					this.handleError(error);
					return of([]);
				})
			);
	}

	/**
	 * Get the RFI matching RFI Procore Id
	 * See Procore docs: https://developers.procore.com/reference/rfis#show-rfi
	 *
	 * @param procoreProjectId string with Procore project Id
	 * @param procoreCompanyId  string with Procore company Id
	 * @returns the RFI matching RFI id.
	 */
	public getRFI(procoreRFId: string, procoreProjectId: string, procoreCompanyId: string) {
		return this.http
			.post<IProcoreRFI>(PROCORE_GET_RFI_ROUTE(procoreProjectId, procoreCompanyId, procoreRFId), {})
			.pipe(
				catchError((error) => {
					this.logger.error('Error during get RFIs:', error);
					this.handleError(error);
					return of(null);
				})
			);
	}

	/**
	 * Creates a new RFI.
	 * See Procore docs: https://developers.procore.com/reference/rfis#create-rfi
	 *
	 * @param procoreProjectId string with Procore project Id
	 * @param procoreCompanyId  string with Procore company Id
	 * @returns the just created RFI.
	 */
	public createNewRFI(newRFI: IProcoreRFIInput, procoreProjectId: string, procoreCompanyId: string) {
		return this.http
			.put<IProcoreRFI>(PROCORE_CREATE_RFI_ROUTE(procoreProjectId, procoreCompanyId), newRFI)
			.pipe(
				catchError((error) => {
					this.logger.error('Error during create new RFIs:', error);
					this.handleError(error);
					return of(null);
				})
			);
	}

	private handleError(error: HttpErrorResponse) {
		if (error.status >= 400 && error.status < 500) {
			this.bannerMessage.displayErrorMessage(new Error(PROCORE_PROJECT_ACCESS_DENIED_REAUTHENTICATE));
		} else {
			this.bannerMessage.displayErrorMessage(error);
		}
	}
}
